import { useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactCard from '../components/ContactCard';

const services = [
  {
    title: 'SEO Optimization',
    description: 'Improve your rankings, increase visibility, and drive consistent organic traffic.',
    link: '/seo-services',
    points: ['Technical + on-page audits', 'Keyword + content mapping', 'Monthly performance reviews'],
  },
  {
    title: 'PPC Advertising',
    description: 'Launch campaigns that convert across Google, Facebook, Instagram, and LinkedIn.',
    link: '/ppc-services',
    points: ['Funnel & landing page strategy', 'Daily optimisation & split-testing', 'Live dashboards for results'],
  },
  {
    title: 'Web Development',
    description: 'Design and build fast, modern websites tailored to your brand and goals.',
    link: '/web-development',
    points: ['Conversion-first UX', 'Lightning-fast performance', 'CMS training & handover'],
  },
  {
    title: 'Content Creation',
    description: 'Strategic video, copy, and design that keeps your brand top of mind.',
    link: '/content-creation',
    points: ['Editorial planning', 'Short-form + long-form content', 'Multi-platform distribution'],
  },
  {
    title: 'Social Media Management',
    description: 'Let us handle the posting, engagement, and growth so you can focus on the business.',
    link: '/social-media',
    points: ['Channel strategy & positioning', 'Creative production', 'Community management'],
  },
  {
    title: 'Email & Lifecycle Marketing',
    description: 'Automated email journeys and campaigns that turn subscribers into customers.',
    link: '/email-marketing',
    points: ['Lifecycle mapping', 'CRM + automation builds', 'Copywriting & design'],
  },
];

export default function ServicesPage() {
  const contactRef = useRef(null);

  const handleTalkClick = () => {
    contactRef.current?.focusForm?.();
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-white via-sky-50 to-indigo-100 font-sans text-slate-900">
      <Navbar onTalkClick={handleTalkClick} />

      <main className="flex-1 pt-32">
        <section className="mx-auto max-w-5xl px-6 text-center">
          <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Every channel, one partner</p>
          <h1 className="mt-4 text-4xl font-semibold md:text-5xl">Digital services that compound your growth</h1>
          <p className="mt-4 text-lg text-slate-600 md:text-xl">
            Whether you need traffic, qualified leads, or a conversion-first website — our team plugs in where you
            need us and ships outcomes fast.
          </p>
        </section>

        <section className="mx-auto mt-16 max-w-6xl px-6">
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {services.map((service) => (
              <article
                key={service.title}
                className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white/70 p-6 text-left shadow-sm backdrop-blur transition hover:-translate-y-1 hover:shadow-md"
              >
                <h2 className="text-2xl font-semibold text-slate-900">{service.title}</h2>
                <p className="mt-3 text-slate-600">{service.description}</p>
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  {service.points.map((point) => (
                    <li key={point} className="flex items-start gap-2">
                      <span className="mt-1 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-500" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={service.link}
                  className="mt-6 inline-flex items-center text-sm font-semibold text-blue-600 transition hover:text-blue-700"
                >
                  Explore service →
                </Link>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-4xl px-6 text-center">
          <div className="rounded-3xl border border-blue-100 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-3xl font-semibold text-slate-900 md:text-4xl">Ready for measurable growth?</h2>
            <p className="mt-3 text-lg text-slate-600">
              Book a 20-minute call and we’ll map the fastest path to more leads, better conversions, and happier
              customers.
            </p>
            <button
              type="button"
              onClick={handleTalkClick}
              className="mt-6 inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-white shadow hover:opacity-90"
            >
              Book a strategy call →
            </button>
          </div>
        </section>
      </main>

      <ContactCard ref={contactRef} className="mt-16" />
      <Footer />
    </div>
  );
}
